import React, { useState } from 'react';

export const ConfigDiffViewer: React.FC = () => {
  const [activeDevice, setActiveDevice] = useState<'cisco-bgp' | 'juniper-vlan'>('cisco-bgp');

  return (
    <div className="flat-card diff-minimal">
      <div className="diff-head-row">
        <div>
          <div className="pill-tag mb-2">
            <span className="status-dot amber" />
            <span>CONFIGURATION AUDITING</span>
          </div>
          <h3>Continuous Configuration Change Tracking</h3>
          <p className="subtext">
            MUSE Telemetry snapshot-versions running device configs to correlate stealth edits with outages.
          </p>
        </div>

        <div className="device-tabs">
          <button
            onClick={() => setActiveDevice('cisco-bgp')}
            className={`tab-btn ${activeDevice === 'cisco-bgp' ? 'active' : ''}`}
          >
            BGP Stanza Audit
          </button>
          <button
            onClick={() => setActiveDevice('juniper-vlan')}
            className={`tab-btn ${activeDevice === 'juniper-vlan' ? 'active' : ''}`}
          >
            VLAN Config Diff
          </button>
        </div>
      </div>

      <div className="meta-audit-bar">
        <div className="m-pill"><span className="status-dot blue" /> Today at 02:14:09 EST</div>
        <div className="m-pill"><span className="status-dot amber" /> Operator SSH: admin_op</div>
        <div className="m-pill"><span className="status-dot red" /> Correlated Outage: Route Blackhole</div>
      </div>

      <div className="diff-frame">
        {activeDevice === 'cisco-bgp' ? (
          <div className="diff-grid">
            <div className="pane">
              <div className="pane-head">Baseline (17:00 EST)</div>
              <pre className="code">
{`router bgp 65001
 bgp log-neighbor-changes
 neighbor 198.51.100.1 remote-as 64501
 neighbor 198.51.100.1 description Core IXP Link
 neighbor 198.51.100.1 route-map IXP-IMPORT-IN in
 neighbor 198.51.100.1 maximum-prefix 5000 80`}
              </pre>
            </div>

            <div className="pane">
              <div className="pane-head alert">Running Config (02:14 EST)</div>
              <pre className="code">
{`router bgp 65001
 bgp log-neighbor-changes
 neighbor 198.51.100.1 remote-as 64501
 neighbor 198.51.100.1 description Core IXP Link
`}
<span className="diff-del">- neighbor 198.51.100.1 route-map IXP-IMPORT-IN in</span>
<span className="diff-add">+ neighbor 198.51.100.1 route-map BLACKHOLE-ALL in  # STEALTH CHANGE</span>
<span className="diff-del">- neighbor 198.51.100.1 maximum-prefix 5000 80</span>
<span className="diff-add">+ neighbor 198.51.100.1 maximum-prefix 100 80      # THRESHOLD LOWERED</span>
              </pre>
            </div>
          </div>
        ) : (
          <div className="diff-grid">
            <div className="pane">
              <div className="pane-head">Baseline (17:00 EST)</div>
              <pre className="code">
{`vlans {
    CUSTOMER-FIBER-SEGMENT-A {
        vlan-id 1040;
        l3-interface vlan.1040;
    }
}`}
              </pre>
            </div>
            <div className="pane">
              <div className="pane-head alert">Running Config (02:14 EST)</div>
              <pre className="code">
{`vlans {
    CUSTOMER-FIBER-SEGMENT-A {
`}
<span className="diff-del">-       vlan-id 1040;</span>
<span className="diff-add">+       vlan-id 9999;  # MISCONFIGURED VLAN ID</span>
{`        l3-interface vlan.1040;
    }
}`}
              </pre>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .diff-minimal {
          margin: 2rem 0;
        }

        .diff-head-row {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 1.5rem;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
        }

        .diff-head-row h3 {
          font-size: 1.4rem;
          margin-bottom: 0.3rem;
        }

        .device-tabs {
          display: flex;
          gap: 0.4rem;
        }

        .tab-btn {
          background: #111111;
          border: 1px solid var(--border-color);
          color: var(--text-muted);
          font-size: 0.78rem;
          padding: 0.4rem 0.8rem;
          border-radius: var(--radius-sm);
          cursor: pointer;
        }

        .tab-btn.active {
          background: #ffffff;
          color: #000000;
          font-weight: 600;
          border-color: #ffffff;
        }

        .meta-audit-bar {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
          flex-wrap: wrap;
        }

        .m-pill {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          font-family: var(--font-mono);
          font-size: 0.75rem;
          color: var(--text-muted);
          background: #050505;
          padding: 0.3rem 0.65rem;
          border-radius: 4px;
          border: 1px solid var(--border-color);
        }

        .diff-frame {
          background: #050505;
          border: 1px solid var(--border-color);
          border-radius: var(--radius-sm);
          overflow: hidden;
        }

        .diff-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1px;
          background: var(--border-color);
        }

        .pane {
          background: #050505;
          padding: 1rem;
        }

        .pane-head {
          font-family: var(--font-mono);
          font-size: 0.72rem;
          color: var(--text-dim);
          margin-bottom: 0.75rem;
          text-transform: uppercase;
        }

        .pane-head.alert {
          color: #ffffff;
        }

        .code {
          font-family: var(--font-mono);
          font-size: 0.78rem;
          line-height: 1.6;
          color: var(--text-muted);
          white-space: pre-wrap;
        }

        .diff-del {
          display: block;
          color: #ef4444;
          background: rgba(239, 68, 68, 0.08);
          padding: 0.1rem 0.3rem;
        }

        .diff-add {
          display: block;
          color: #10b981;
          background: rgba(16, 185, 129, 0.08);
          padding: 0.1rem 0.3rem;
        }

        @media (max-width: 800px) {
          .diff-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};
